import React from 'react'
import { gql } from '@apollo/client'
import isEmpty from 'lodash/isEmpty'
import type { RouteDef, WorkOrder } from '@ztechapps/common.contract'
import { useDocument } from '@ztechapps/common.hook'
import { useInject, WebuiIocKey } from '@ztechapps/webui.contract'
import { LocalDate, LocalDateTime } from '@ztechapps/webui.date'
import { Tags } from '@ztechapps/webui.input'
import { EntityCodeLink, EntityPreview, ShowIf } from '@ztechapps/webui.util'
import { WorkOrderStatus, WorkOrderStatusBadge } from '../WorkOrderStatus'

const workOrderPreviewQuery = gql`
  query singleWorkOrder($filter: JSON) {
    singleWorkOrder(filter: $filter) {
      _id
      code
      status
      quantity
      product {
        _id
        code
        name
      }
      createdAt
      completionDate
      tags
    }
  }
`

const WorkOrderPreview = ({ _id, code }: { _id: string; code: string }) => {
  const workOrderDocumentResult = useDocument<WorkOrder>({ _id, query: workOrderPreviewQuery })
  const workOrder = workOrderDocumentResult.document
  const quantity = workOrder?.quantity ?? 0

  return (
    <EntityPreview
      code={code}
      statusBadge={<WorkOrderStatusBadge status={workOrder?.status} />}
      loading={workOrderDocumentResult.loading}
      title={workOrder?.product?.code}
      subtitle={workOrder?.product?.name}
      rows={[
        { label: 'Qty to Manufacture', value: `${quantity} ${quantity > 1 ? 'units' : 'unit'}` },
        // `LocalDate`/`LocalDateTime` always render SOMETHING (their own "--" for a missing
        // date), so `EntityPreview`'s `?? '---'` fallback never sees a falsy value to catch —
        // checked here instead, same as this row set's own Tags row below.
        {
          label: 'Date Added',
          value: workOrder?.createdAt ? <LocalDateTime value={workOrder?.createdAt} /> : undefined,
        },
        {
          label: 'Completion Date',
          value: workOrder?.completionDate ? <LocalDate value={workOrder?.completionDate} /> : undefined,
        },
        {
          label: 'Tags',
          // `Tags` renders an empty (but truthy) wrapper for an empty array, so `EntityPreview`'s
          // own `?? '---'` fallback never sees a falsy value to catch — checked here instead.
          value: isEmpty(workOrder?.tags) ? undefined : (
            <Tags dataArray={workOrder?.tags} className="justify-content-end" />
          ),
        },
      ]}
    />
  )
}

export type WorkOrderCodeLinkProps = { _id: string; code: string; status?: string }

export const WorkOrderCodeLink = ({ _id, code, status }: WorkOrderCodeLinkProps) => {
  const workOrderItemRoute = useInject<RouteDef>(WebuiIocKey.routes.workOrderItem)

  return (
    <EntityCodeLink
      code={code}
      statusDot={
        <ShowIf condition={!!status}>
          <WorkOrderStatus status={status ?? ''} />
        </ShowIf>
      }
      to={workOrderItemRoute.altPath({ _id })}
      target="_blank"
      preview={<WorkOrderPreview _id={_id} code={code} />}
    />
  )
}
